import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  breadcrumbItemRecipe,
  breadcrumbLinkRecipe,
  breadcrumbsRecipe,
} from '../recipes/nav.recipe';

export interface BreadcrumbItem {
  label: React.ReactNode;
  /** Where the crumb leads. Omit for the page you are on, and for a section with no page of its own. */
  href?: string;
}

export interface BreadcrumbsProps extends React.HTMLAttributes<HTMLElement> {
  /** Outermost first; the last is the page you are on. */
  items: BreadcrumbItem[];
  /**
   * Draws a crumb's link the way the app's router wants. Without it a crumb
   * is a plain anchor, which reloads the page.
   */
  renderLink?: (
    href: string,
    body: React.ReactNode,
    className: string
  ) => React.ReactNode;
}

/**
 * Where the page is, along the top of the dashboard.
 *
 *   <Breadcrumbs
 *     items={[{ label: 'Call Log', href: '/call-log' }, { label: 'CL-1042' }]}
 *     renderLink={(href, body, className) => (
 *       <Link to={href} className={className}>{body}</Link>
 *     )}
 *   />
 *
 * The last crumb is marked as the current page and is never a link, even
 * when given an href: it would only reload what is already open.
 */
export const Breadcrumbs = React.forwardRef<HTMLElement, BreadcrumbsProps>(
  function Breadcrumbs({ items, renderLink, className, ...props }, ref) {
    const link =
      renderLink ??
      ((href: string, body: React.ReactNode, linkClass: string) => (
        // design-tokens-ignore: owned by Breadcrumbs
        <a href={href} className={linkClass}>
          {body}
        </a>
      ));

    return (
      // design-tokens-ignore: owned by Breadcrumbs
      <nav
        ref={ref}
        aria-label="Breadcrumb"
        className={cn('min-w-0', className)}
        {...props}
      >
        {/* design-tokens-ignore: owned by Breadcrumbs */}
        <ol className={breadcrumbsRecipe()}>
          {items.map((item, index) => {
            const last = index === items.length - 1;
            return (
              <React.Fragment key={index}>
                {index > 0 ? (
                  // design-tokens-ignore: owned by Breadcrumbs
                  <li aria-hidden className="shrink-0">
                    •
                  </li>
                ) : null}
                {/* design-tokens-ignore: owned by Breadcrumbs */}
                <li
                  className={breadcrumbItemRecipe()}
                  aria-current={last ? 'page' : undefined}
                >
                  {item.href && !last
                    ? link(item.href, item.label, breadcrumbLinkRecipe())
                    : item.label}
                </li>
              </React.Fragment>
            );
          })}
        </ol>
      </nav>
    );
  }
);
